import moment from "moment";

function toSortableDate(value) {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
}

function formatWith(value, formatter) {
  if (!value) return "-";
  if (typeof formatter === "function") return formatter(value);
  return value;
}

function toSlug(value, fallback) {
  const source = value || fallback;
  return String(source)
    .toLowerCase()
    .replace(/\s+/g, "-");
}

function toToday() {
  return new Date().toISOString().slice(0, 10);
}

export function getDepartureDateRange(startDate, endDate) {
  if (!startDate || !endDate) return [];

  const dates = [];
  const cursor = moment(startDate);

  if (!cursor.isValid()) return [];

  while (cursor.isBefore(endDate)) {
    dates.push(cursor.format("YYYY-MM-DD"));
    cursor.add(1, "days");
  }

  return dates;
}

export function getMissingDepartureDates(
  startDate,
  endDate,
  existingDates = []
) {
  const existingSet = new Set((existingDates || []).filter(Boolean));
  return getDepartureDateRange(startDate, endDate).filter(
    date => !existingSet.has(date)
  );
}

export function groupReservationsByDeparture(rows = [], formatDate) {
  const grouped = {};
  (Array.isArray(rows) ? rows : []).forEach(row => {
    const key = row.departure_id
      ? `dep-${row.departure_id}`
      : `tour-${row.tour_id || row.tour_text}`;

    if (!grouped[key]) {
      grouped[key] = {
        key,
        tourTitle: row.tour_title || row.tour_text || "Unknown Tour",
        departureLabel: row.departure_start_date
          ? `${formatWith(row.departure_start_date, formatDate)} - ${
              row.departure_end_date
                ? formatWith(row.departure_end_date, formatDate)
                : "-"
            }`
          : "-",
        peopleCount:
          typeof row.departure_num_passengers === "number"
            ? row.departure_num_passengers
            : null,
        rows: []
      };
    }

    grouped[key].rows.push(row);
  });

  return Object.values(grouped).sort((a, b) => {
    const aDate =
      a.rows && a.rows.length
        ? toSortableDate(a.rows[0].date)
        : Number.MAX_SAFE_INTEGER;
    const bDate =
      b.rows && b.rows.length
        ? toSortableDate(b.rows[0].date)
        : Number.MAX_SAFE_INTEGER;
    return aDate - bDate;
  });
}

export function mapRestaurantReservationRows(
  departureGroups = [],
  getTourText
) {
  const rows = [];
  (Array.isArray(departureGroups) ? departureGroups : []).forEach(group => {
    const departure = group && group.departure ? group.departure : {};
    const dinners = Array.isArray(group && group.dinners) ? group.dinners : [];

    dinners.forEach(dinner => {
      rows.push({
        key: `restaurant-${departure.id || "dep"}-${dinner.id ||
          dinner.date}-${dinner.time || "notime"}`,
        confirmed: Boolean(dinner.confirmed),
        date: dinner.date || departure.start_date,
        tour_id: departure.tour_id,
        tour_text:
          typeof getTourText === "function" ? getTourText(departure) : "",
        time: dinner.time || "",
        reservation_number: dinner.reservation_number || null,
        notes: dinner.notes || null,
        departure_id: departure.id,
        departure_start_date: departure.start_date || null,
        departure_end_date: departure.end_date || null,
        departure_num_passengers:
          typeof departure.num_passengers === "number"
            ? departure.num_passengers
            : null,
        tour_title: departure.tour,
        dinner_id: dinner.id,
        canManage: Boolean(departure.id && dinner.id)
      });
    });
  });
  return rows;
}

export function mapAccomReservationRows(departureGroups = [], getTourText) {
  const rows = [];
  (Array.isArray(departureGroups) ? departureGroups : []).forEach(group => {
    const departure = group && group.departure ? group.departure : {};
    const reservations = Array.isArray(group && group.reservations)
      ? group.reservations
      : [];

    reservations.forEach(reservation => {
      rows.push({
        key: `accom-${departure.id || "dep"}-${reservation.id ||
          reservation.date}-${reservation.time || "notime"}`,
        confirmed: Boolean(reservation.confirmed),
        booked: Boolean(reservation.booked),
        paid: Boolean(reservation.paid),
        date: reservation.date || departure.start_date,
        tour_id: departure.tour_id,
        tour_text:
          typeof getTourText === "function" ? getTourText(departure) : "",
        room_type_twin: reservation.room_type_twin || "0",
        room_type_double: reservation.room_type_double || "0",
        room_type_single: reservation.room_type_single || "0",
        room_type_family: reservation.room_type_family || "0",
        notes: reservation.notes || null,
        departure_id: departure.id,
        departure_start_date: departure.start_date || null,
        departure_end_date: departure.end_date || null,
        departure_num_passengers:
          typeof departure.num_passengers === "number"
            ? departure.num_passengers
            : null,
        tour_title: departure.tour,
        reservation_id: reservation.id,
        canManage: Boolean(departure.id && reservation.id)
      });
    });
  });
  return rows;
}

export const restaurantReservationsCsvHeaders = [
  "Reservation #",
  "Tour",
  "Departure Start",
  "Departure End",
  "Confirmed",
  "Date",
  "Night of",
  "Booking Time",
  "Notes"
];

export const accomReservationsCsvHeaders = [
  "Confirmed",
  "Booked",
  "Paid",
  "Night Of",
  "Date",
  "Twin",
  "Double",
  "Single",
  "Family",
  "Tour",
  "Departure Start",
  "Departure End",
  "Notes"
];

export function buildRestaurantReservationsCsv({
  rows = [],
  restaurantName,
  getDayFromDate
}) {
  const dayFormatter =
    typeof getDayFromDate === "function" ? getDayFromDate : () => "";
  return {
    headers: restaurantReservationsCsvHeaders,
    rows: rows.map(row => [
      row.reservation_number || "",
      row.tour_title || row.tour_text || "",
      row.departure_start_date || "",
      row.departure_end_date || "",
      row.confirmed ? "Yes" : "No",
      row.date || "",
      dayFormatter(row.date) || "",
      row.time || "",
      row.notes || ""
    ]),
    filename: `reservations-${toSlug(
      restaurantName,
      "restaurant"
    )}-${toToday()}.csv`
  };
}

export function buildAccomReservationsCsv({
  rows = [],
  accomName,
  getDayFromDate
}) {
  const dayFormatter =
    typeof getDayFromDate === "function" ? getDayFromDate : () => "";
  return {
    headers: accomReservationsCsvHeaders,
    rows: rows.map(row => [
      row.confirmed ? "Yes" : "No",
      row.booked ? "Yes" : "No",
      row.paid ? "Yes" : "No",
      dayFormatter(row.date) || "",
      row.date || "",
      row.room_type_twin || "0",
      row.room_type_double || "0",
      row.room_type_single || "0",
      row.room_type_family || "0",
      row.tour_title || row.tour_text || "",
      row.departure_start_date || "",
      row.departure_end_date || "",
      row.notes || ""
    ]),
    filename: `reservations-${toSlug(accomName, "accom")}-${toToday()}.csv`
  };
}
